/**
 * Web-local mirror of `packages/shared-utils/src/legal-versions.ts`'s
 * `CURRENT_TERMS_VERSION`, needed by `sign-up.lazy.tsx` BEFORE an account
 * (and therefore a session to call the protected `getTermsStatus` tRPC
 * procedure with) exists.
 *
 * NOT re-exported from `@xeito/shared-utils` here: this worktree's
 * `node_modules/@xeito/shared-utils` symlink resolves through to the
 * PRIMARY checkout's copy of that package (see
 * `apps/api/src/__tests__/user-router.test.ts`'s top-of-file comment for
 * the full mechanism), which predates this branch and doesn't export
 * `CURRENT_TERMS_VERSION` yet — importing it here would build and test fine
 * against THIS worktree's `packages/shared-utils` source but break the
 * moment the same code runs against the resolved (primary) module, exactly
 * the failure mode already documented for the API router. A public tRPC
 * endpoint was considered and rejected: it would add a network round trip
 * to the sign-up critical path to learn a value that's a build-time
 * constant on the same branch. A literal is the least-surprising option
 * available under the constraint.
 *
 * MUST be bumped in lockstep with `packages/shared-utils/src/legal-
 * versions.ts` and the marketing terms-of-service.astro `TERMS_LAST_UPDATED`
 * whenever the Terms text changes materially. The web page's own display
 * date tracks that page's content, not this version. This
 * duplication is a follow-up to collapse once the primary checkout has
 * merged this branch (tracked in the same roadmap row, B-new-900).
 */
export const CURRENT_TERMS_VERSION = '2026-10-07'

/**
 * B-new-900 — post-auth Terms/Privacy acceptance gate.
 *
 * Pure helpers, mirroring `lifecycle-email-optout.ts`'s shape: no DOM/router
 * access beyond the strings passed in, so the interesting branches are unit
 * testable without rendering the provider tree.
 */

/** Routes that must never show the acceptance screen, even mid-load or
 *  errored: sign-in/up, the OAuth + magic-link callback/verify hops, and
 *  the two standalone pages a signed-out (or not-yet-consented) visitor can
 *  legitimately reach. Matched against the path with the `/{locale}` prefix
 *  already stripped. */
const PUBLIC_PATH_PREFIXES = [
  '/sign-in',
  '/sign-up',
  '/oauth/callback',
  '/verify-email',
  '/email-preferences',
  '/accessibility-statement',
]

/** B-new-9684 — exit pages a user who has NOT accepted the terms must still
 *  reach: cancelling a subscription and exercising the right of withdrawal
 *  (TRLGDCU art. 62.3 for cancellation, arts. 68-79 for withdrawal: neither is
 *  conditioned on accepting terms). Matched
 *  exactly (plus sub-paths) against the path with the locale prefix stripped.
 *  These pages render without acceptance, so every tRPC procedure they call
 *  must be exempt on the server gate too: cancel, withdraw and
 *  withdrawalEligibility already are; `subscription.getSubscription` is the
 *  one the subscription page cannot work without. */
const EXIT_PATH_PREFIXES = ['/dashboard/subscription', '/withdrawal-rights']

/** Admin routes stay reachable for an ADMIN_EMAIL-allowlisted operator even
 *  with `needsAcceptance: true` — see the task note on not locking out the
 *  admin. Scoped to `/admin` only, not "admins skip the gate everywhere". */
const ADMIN_PATH_PREFIX = '/admin'

/** Strips a leading `/{locale}` segment (`es`, `gl`, `en`, `de`, `fr`, `it`,
 *  `nl`, `pt`) from a pathname, e.g. `/es/sign-up` -> `/sign-up`. Unknown
 *  first segments (a locale-less path) are returned unchanged. */
const SUPPORTED_LOCALES = ['es', 'gl', 'en', 'de', 'fr', 'it', 'nl', 'pt']
export function stripLocalePrefix(pathname: string): string {
  const parts = pathname.split('/')
  if (parts.length > 1 && SUPPORTED_LOCALES.includes(parts[1])) {
    return '/' + parts.slice(2).join('/')
  }
  return pathname
}

export function isPublicTermsRoute(pathname: string): boolean {
  const path = stripLocalePrefix(pathname) || '/'
  return PUBLIC_PATH_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))
}

export function isTermsExitRoute(pathname: string): boolean {
  const path = stripLocalePrefix(pathname) || '/'
  return EXIT_PATH_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))
}

/** First path segment if it is a supported locale, else null. */
export function localeFromPathname(pathname: string): string | null {
  const first = pathname.split('/')[1]
  return first && SUPPORTED_LOCALES.includes(first) ? first : null
}

export function isAdminTermsRoute(pathname: string): boolean {
  const path = stripLocalePrefix(pathname) || '/'
  return path === ADMIN_PATH_PREFIX || path.startsWith(`${ADMIN_PATH_PREFIX}/`)
}

/**
 * Magic-link sign-ups tick the terms checkbox before the account exists
 * (B-new-900 #3). The callback URL carries `termsV=<version>` (mirroring
 * B-new-870's `lcOptOut`) so the first authenticated landing can call
 * `acceptTerms` automatically instead of showing the screen.
 */
export function readTermsVersionParam(search: string): string | null {
  return new URLSearchParams(search).get('termsV')
}

/** Strips `termsV` from a query string, preserving every other param. */
export function stripTermsVersionParam(pathname: string, search: string): string {
  const params = new URLSearchParams(search)
  if (!params.has('termsV')) return `${pathname}${search}`
  params.delete('termsV')
  return params.toString() ? `${pathname}?${params}` : pathname
}

/** B-new-9684 — fail-closed read of a `user.getTermsStatus` result for pages
 *  that render without the gate (the exit routes). Only an explicit
 *  `needsAcceptance === false` counts as accepted; unknown, null, errored or
 *  malformed status counts as NOT accepted. */
export function isTermsNotAccepted(status: unknown): boolean {
  return (status as { needsAcceptance?: unknown } | null | undefined)?.needsAcceptance !== false
}
